import { useState, type ReactNode } from 'react';
import { Box, Popover, Portal } from '@chakra-ui/react';
import { IconButton, type IconButtonProps } from '@/components/ui/icon-button';
import { FiMoreHorizontal } from 'react-icons/fi';
import { CONTROL_SURFACE_SHADOW } from '@/components/ui/shadows';
import { Tooltip } from '@/components/ui/tooltip';

type PopoverPlacement = NonNullable<React.ComponentProps<typeof Popover.Root>['positioning']>['placement'];

interface OverflowActionsMenuProps {
	/** Accessible label for the trigger button. */
	ariaLabel: string;
	/** Action rows — compose with OverflowActionItem / OverflowActionSeparator. */
	children: ReactNode;
	/** Trigger size; defaults to "lg" to match the drawer footers. */
	size?: 'sm' | 'md' | 'lg';
	placement?: PopoverPlacement;
	/** Compact ghost treatment for a drawer header rather than a footer action surface. */
	header?: boolean;
	/** Hover surface for a header trigger when its surrounding header uses a contrasting fill. */
	headerHoverBg?: React.ComponentProps<typeof IconButton>['bg'];
	/** Local trigger styling for a host that needs to match adjacent controls. */
	triggerProps?: IconButtonProps;
	/**
	 * Names the trigger on hover. Held here rather than by the host because it has to know when the
	 * popover is open — a tooltip left live behind an open menu sits over the actions it named.
	 */
	tooltip?: string;
}

/**
 * A "more actions" overflow trigger that opens a popover of actions. Drawer headers use
 * the compact ghost treatment; action footers keep the raised outlined treatment.
 */
const OverflowActionsMenu = (props: OverflowActionsMenuProps) => {
	const {
		ariaLabel,
		children,
		size = 'lg',
		placement = 'top-start',
		header = false,
		headerHoverBg = 'surface.default',
		triggerProps,
		tooltip,
	} = props;

	const [isOpen, setIsOpen] = useState(false);

	const menu = (
		<Popover.Root
			open={isOpen}
			onOpenChange={({ open }) => setIsOpen(open)}
			positioning={{ placement: header && placement === 'top-start' ? 'bottom-end' : placement }}
		>
			<Popover.Trigger asChild>
				<IconButton
					aria-label={ariaLabel}
					size={size}
					variant={header ? 'ghost' : 'outline'}
					borderColor={header ? 'transparent' : 'border.default'}
					bg={header ? 'transparent' : 'surface.default'}
					color="brand.fg"
					boxShadow={header ? 'none' : CONTROL_SURFACE_SHADOW}
					_hover={header ? { bg: headerHoverBg } : { bg: 'brand.solid', color: 'brand.contrast' }}
					flexShrink={0}
					{...triggerProps}
				>
					<FiMoreHorizontal />
				</IconButton>
			</Popover.Trigger>
			<Portal>
				<Popover.Positioner>
					<Popover.Content layerStyle="card.raised" p={1} minW={40}>
						{children}
					</Popover.Content>
				</Popover.Positioner>
			</Portal>
		</Popover.Root>
	);

	// Wrapped from outside rather than around the trigger: `Popover.Trigger asChild` already claims
	// that element, and a second `asChild` over it left the button inert.
	if (!tooltip) return menu;

	return (
		<Tooltip content={tooltip} openDelay={300} disabled={isOpen}>
			<Box as="span" display="inline-flex">
				{menu}
			</Box>
		</Tooltip>
	);
};

export default OverflowActionsMenu;
